import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { AssetEquipmentItem } from '../../hooks/useAssetEquipment'

/** assetUid → name of the assembly it is aggregated into. */
export type EquipmentViaMap = Record<string, string>

const EquipmentViaContext = createContext<EquipmentViaMap>({})

/**
 * Explains "why is this here?" rows on an ancestor-scoped equipment listing.
 *
 * The Equipment tab queries `?ancestor_uid=<focal>`, which matches on
 * `parent_asset_ids` — a column the aggregating rollup extends. So a radiator
 * physically parented in another building legitimately shows up under a
 * building whose heating network it is `part_of`. Those rows get a link marker
 * naming the assembly (spec 2026-08-16-03 §6).
 *
 * A row is flagged when BOTH hold: its physical parent is not part of the
 * returned subtree, and it has an outgoing aggregating relation.
 */
export function computeEquipmentVia(
    focalAssetUid: string | undefined,
    equipment: AssetEquipmentItem[],
): EquipmentViaMap {
    if (!focalAssetUid) return {}
    const inSubtree = new Set<string>(equipment.map((e) => e.uid))
    inSubtree.add(focalAssetUid)

    const via: EquipmentViaMap = {}
    for (const item of equipment) {
        if (item.parent_asset_uid && inSubtree.has(item.parent_asset_uid)) continue
        const rel = (item.relations?.outgoing ?? []).find(
            (r) => r.relation_type_details?.aggregating && r.target,
        )
        if (rel?.target) via[item.uid] = rel.target.name || rel.target.uid
    }
    return via
}

export function EquipmentViaProvider({
    focalAssetUid,
    equipment,
    children,
}: {
    focalAssetUid: string | undefined
    equipment: AssetEquipmentItem[]
    children: ReactNode
}) {
    const value = useMemo(
        () => computeEquipmentVia(focalAssetUid, equipment),
        [equipment, focalAssetUid],
    )
    return (
        <EquipmentViaContext.Provider value={value}>{children}</EquipmentViaContext.Provider>
    )
}

/** Name of the assembly an equipment row arrived through, if any. */
export function useEquipmentVia(assetUid: string): string | undefined {
    return useContext(EquipmentViaContext)[assetUid]
}
